import React, { useMemo } from "react"

import { Button } from "@coval/ui"
import { ChartFigure, type ChartTable } from "@coval/ui/chart-figure"

import type {
  ChartClickData,
  ChartDataPoint,
  ChartWidgetConfig,
} from "../../types/chart"
import type { DisplayNameLookups } from "../../utils/chart/displayNameUtils"
import { buildChartWidgetFigure } from "./chartWidgetFigureData"

interface ChartWidgetFigureProps {
  label: string
  config: ChartWidgetConfig
  chartData: ChartDataPoint[]
  splitKeys: string[]
  displayNameLookups: DisplayNameLookups | null
  /** The widget's date range, which whole-range drill-downs cover. */
  dateRange?: { start: Date; end: Date } | null
  /** When set, each table value opens the drill-down its chart mark opens. */
  onPointClick?: (point: ChartClickData) => void
  children: React.ReactNode
}

/**
 * Names a dashboard chart after its widget and offers what it plots as a
 * table: buckets by series over time, totals per series for pie, top-list and
 * whole-range bar charts, and bins for a histogram.
 */
export const ChartWidgetFigure: React.FC<ChartWidgetFigureProps> = ({
  label,
  config,
  chartData,
  splitKeys,
  displayNameLookups,
  dateRange,
  onPointClick,
  children,
}) => {
  const model = useMemo(
    () =>
      buildChartWidgetFigure({
        config,
        chartData,
        splitKeys,
        displayNameLookups,
        dateRange,
        drillDown: Boolean(onPointClick),
      }),
    [chartData, config, dateRange, displayNameLookups, onPointClick, splitKeys],
  )

  const table = useMemo(
    (): ChartTable => ({
      columns: model.columns,
      rows: model.rows.map((row) => ({
        key: row.key,
        cells: Object.fromEntries(
          Object.entries(row.cells).map(([key, cell]) => {
            if (typeof cell === "string") {
              return [key, cell]
            }
            const { click } = cell
            return [
              key,
              click && onPointClick ? (
                <Button
                  type="button"
                  variant="link"
                  aria-label={cell.actionLabel}
                  onClick={() => onPointClick(click)}
                >
                  {cell.text}
                </Button>
              ) : (
                cell.text
              ),
            ]
          }),
        ),
      })),
    }),
    [model, onPointClick],
  )

  return (
    <ChartFigure
      label={label}
      summary={model.summary}
      table={table}
      className="h-full w-full"
    >
      {children}
    </ChartFigure>
  )
}
